import { Link, Outlet, useLocation } from 'react-router-dom';
import {
  HomeIcon,
  KeyIcon,
  UserIcon,
  CreditCardIcon,
  DocumentChartBarIcon,
  Cog6ToothIcon,
  BellIcon,
  MagnifyingGlassIcon,
  ShieldCheckIcon,
  CpuChipIcon,
} from '@heroicons/react/24/outline';
import NotificationDropdown from '../components/NotificationDropdown';
import UserDropdown from '../components/UserDropdown';

const navigation = [
  // Primary
  { name: 'Dashboard', to: '/', icon: HomeIcon },
  { name: 'Analytics', to: '/analytics', icon: DocumentChartBarIcon },
  
  // Development
  { name: 'API Keys', to: '/api-keys', icon: KeyIcon },
  { name: 'Models', to: '/models', icon: CpuChipIcon },
  
  // User
  { name: 'Profile', to: '/profile', icon: UserIcon },
  { name: 'Account', to: '/account', icon: CreditCardIcon },
  { name: 'Security', to: '/security', icon: ShieldCheckIcon },
  { name: 'Notifications', to: '/notifications', icon: BellIcon },
  
  // System
  { name: 'Settings', to: '/settings', icon: Cog6ToothIcon },
];

export default function RootLayout() {
  const location = useLocation();

  return (
    <div className="min-h-screen">
      {/* Side Navigation */}
      <nav className="fixed top-0 left-0 h-full w-64 bg-white/[0.02] backdrop-blur-2xl border-r border-white/[0.05]">
        {/* Logo */}
        <div className="h-16 flex items-center px-6 border-b border-white/[0.05]">
          <img
            className="h-8 w-auto"
            src="/vite.svg"
            alt="Magick"
          />
          <span className="ml-3 text-xl font-medium text-white">
            Magick
          </span>
        </div>
        
        {/* Navigation Items */}
        <div className="mt-2">
          {/* Primary Section */}
          <div className="px-6 py-2 text-xs font-medium text-white/30 uppercase tracking-wider">
            Primary
          </div>
          {navigation.slice(0, 2).map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.to;
            return (
              <div key={item.name}>
                <Link
                  to={item.to}
                  className={`flex items-center px-6 py-3.5 text-sm transition-all duration-200
                    ${isActive 
                      ? 'text-white bg-white/[0.08]' 
                      : 'text-white/60 hover:text-white hover:bg-white/[0.04]'
                    }`}
                >
                  <Icon className="h-5 w-5 mr-3" />
                  {item.name}
                </Link>
              </div>
            );
          })}

          <div className="mt-4 px-6 py-2 text-xs font-medium text-white/30 uppercase tracking-wider">
            Development
          </div>
          {navigation.slice(2, 4).map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.to;
            return (
              <div key={item.name}>
                <Link
                  to={item.to}
                  className={`flex items-center px-6 py-3.5 text-sm transition-all duration-200
                    ${isActive 
                      ? 'text-white bg-white/[0.08]' 
                      : 'text-white/60 hover:text-white hover:bg-white/[0.04]'
                    }`}
                >
                  <Icon className="h-5 w-5 mr-3" />
                  {item.name}
                </Link>
              </div>
            );
          })}

          <div className="mt-4 px-6 py-2 text-xs font-medium text-white/30 uppercase tracking-wider">
            User
          </div>
          {navigation.slice(4, 8).map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.to;
            return (
              <div key={item.name}>
                <Link
                  to={item.to}
                  className={`flex items-center px-6 py-3.5 text-sm transition-all duration-200
                    ${isActive 
                      ? 'text-white bg-white/[0.08]' 
                      : 'text-white/60 hover:text-white hover:bg-white/[0.04]'
                    }`}
                >
                  <Icon className="h-5 w-5 mr-3" />
                  {item.name}
                </Link>
              </div>
            );
          })}

          <div className="mt-4 px-6 py-2 text-xs font-medium text-white/30 uppercase tracking-wider">
            System
          </div>
          {navigation.slice(8).map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.to;
            return (
              <div key={item.name}>
                <Link
                  to={item.to}
                  className={`flex items-center px-6 py-3.5 text-sm transition-all duration-200
                    ${isActive 
                      ? 'text-white bg-white/[0.08]' 
                      : 'text-white/60 hover:text-white hover:bg-white/[0.04]'
                    }`}
                >
                  <Icon className="h-5 w-5 mr-3" />
                  {item.name}
                </Link>
              </div>
            );
          })}
        </div>
      </nav>

      {/* Top Bar */}
      <div className="fixed top-0 left-64 right-0 h-16 bg-white/[0.02] backdrop-blur-2xl border-b border-white/[0.05]">
        <div className="h-full px-6 flex items-center justify-end space-x-4">
          {/* Search */}
          <div className="relative flex-1 max-w-lg">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <MagnifyingGlassIcon className="h-5 w-5 text-white/40" aria-hidden="true" />
            </div>
            <input
              type="text"
              className="w-full bg-white/[0.05] border-0 pl-10 pr-3 py-2 text-white placeholder-white/40 focus:ring-0 rounded-lg"
              placeholder="Search..."
            />
          </div>

          <NotificationDropdown />
          <UserDropdown />
        </div>
      </div>

      {/* Main Content */}
      <main className="pl-64 pt-16">
        <div className="max-w-7xl mx-auto p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
